import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add,
  Search,
  Edit,
  Visibility,
  Download,
  Email,
  Payment,
  Delete,
  GetApp,
} from '@mui/icons-material';
import axios from 'axios';

interface Invoice {
  id: number;
  invoice_number: string;
  customer_name: string;
  project_title: string;
  amount: number;
  tax_amount: number;
  total_amount: number;
  status: 'sent' | 'paid' | 'overdue' | 'cancelled';
  due_date: string;
  paid_date?: string;
  created_at: string;
}

export default function Invoices() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/invoices', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      setInvoices(response.data.invoices || []);
    } catch (err: any) {
      console.error('Error fetching invoices:', err);
      setError(err.response?.data?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = 
      invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.project_title.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' } = {
      draft: 'default',
      sent: 'primary',
      paid: 'success',
      overdue: 'error',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  const isOverdue = (dueDate: string, status: string) => {
    return status !== 'paid' && new Date(dueDate) < new Date();
  };

  const getTotalStats = () => {
    // Exclude cancelled invoices from all calculations
    const activeInvoices = invoices.filter(inv => inv.status !== 'cancelled');

    return {
      totalAmount: activeInvoices.reduce((sum, inv) => sum + parseFloat(inv.total_amount.toString()), 0),
      paidAmount: activeInvoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + parseFloat(inv.total_amount.toString()), 0),
      pendingAmount: activeInvoices.filter(inv => inv.status !== 'paid').reduce((sum, inv) => sum + parseFloat(inv.total_amount.toString()), 0),
      overdueCount: activeInvoices.filter(inv => isOverdue(inv.due_date, inv.status)).length,
    };
  };

  const stats = getTotalStats();

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteClick = (invoice: Invoice, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent any row click action
    setInvoiceToDelete(invoice);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (invoiceToDelete) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/invoices/${invoiceToDelete.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          // Remove the invoice from local state
          setInvoices(prev => prev.filter(i => i.id !== invoiceToDelete.id));
          setDeleteDialogOpen(false);
          setInvoiceToDelete(null);
        } else {
          console.error('Failed to delete invoice');
          alert('Failed to delete invoice. Please try again.');
        }
      } catch (error) {
        console.error('Error deleting invoice:', error);
        alert('An error occurred while deleting the invoice.');
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}


      {/* Summary Cards */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' }, 
        flexWrap: 'wrap',
        gap: 3, 
        mb: 3 
      }}>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Value
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="primary">
                {formatCurrency(stats.totalAmount)}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Paid
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {formatCurrency(stats.paidAmount)}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {formatCurrency(stats.pendingAmount)}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Overdue
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="error.main">
                {stats.overdueCount}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, 
            gap: 2, 
            alignItems: { md: 'center' } 
          }}>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
              <TextField
                fullWidth
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 25%' } }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="sent">Sent</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 25%' } }}>
              <Typography variant="body2" color="textSecondary">
                {filteredInvoices.length} invoices found
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Invoice #</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Project</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredInvoices
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((invoice) => {
                const isOverdueRow = invoice.status !== 'paid' && invoice.due_date && new Date(invoice.due_date) < new Date();

                return (
                  <TableRow
                    key={invoice.id}
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      bgcolor: isOverdueRow ? 'error.light' : 'inherit',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                        backgroundColor: isOverdueRow ? 'error.light' : 'action.hover',
                      }
                    }}
                  >
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {invoice.invoice_number}
                  </Typography>
                </TableCell>
                <TableCell>{invoice.customer_name}</TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {invoice.project_title}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {formatCurrency(invoice.total_amount)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Net: {formatCurrency(invoice.amount)} + VAT: {formatCurrency(invoice.tax_amount)}
                  </Typography>
                </TableCell>
                <TableCell>
                  {(() => {
                    const overdueCheck = isOverdue(invoice.due_date, invoice.status);
                    const displayStatus = invoice.status === 'cancelled' ? 'Cancelled' :
                                         (overdueCheck && invoice.status === 'sent') ? 'Overdue' :
                                         invoice.status === 'sent' ? 'Sent' :
                                         invoice.status === 'paid' ? 'Paid' : invoice.status;
                    const bgColor = invoice.status === 'paid' ? 'success.main' :
                                   invoice.status === 'cancelled' ? 'grey.500' :
                                   (overdueCheck && invoice.status === 'sent') ? 'error.main' :
                                   'primary.main';

                    return (
                      <Box
                        sx={{
                          display: 'inline-block',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                          bgcolor: bgColor,
                          color: 'white'
                        }}
                      >
                        {displayStatus}
                      </Box>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  <Typography 
                    variant="body2" 
                    color={
                      isOverdue(invoice.due_date, invoice.status) && invoice.status !== 'paid' 
                        ? 'error' 
                        : 'textPrimary'
                    }
                  >
                    {new Date(invoice.due_date).toLocaleDateString()}
                  </Typography>
                  {invoice.paid_date && (
                    <Typography variant="caption" color="success.main">
                      Paid: {new Date(invoice.paid_date).toLocaleDateString()}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {new Date(invoice.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<GetApp />}
                      onClick={async () => {
                        try {
                          // Find the document associated with this invoice
                          const token = localStorage.getItem('token');
                          const docsResponse = await axios.get('/api/documents', {
                            headers: { 'Authorization': `Bearer ${token}` }
                          });
                          const invoiceDoc = docsResponse.data.documents?.find(
                            (doc: any) => doc.related_to === 'Invoice' && doc.related_id === invoice.id
                          );

                          if (invoiceDoc) {
                            window.open(`/api/documents/download/${invoiceDoc.id}`, '_blank');
                          } else {
                            alert('Invoice document not found');
                          }
                        } catch (error) {
                          console.error('Error downloading invoice:', error);
                          alert('Failed to download invoice');
                        }
                      }}
                    >
                      Download
                    </Button>
                    {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                      <>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          onClick={async () => {
                            if (window.confirm('Mark this invoice as paid?')) {
                              try {
                                const token = localStorage.getItem('token');
                                await axios.put(
                                  `/api/invoices/${invoice.id}`,
                                  {
                                    ...invoice,
                                    status: 'paid',
                                    paid_date: new Date().toISOString().split('T')[0]
                                  },
                                  {
                                    headers: { 'Authorization': `Bearer ${token}` }
                                  }
                                );

                                // Refresh invoices list
                                await fetchInvoices();
                              } catch (error) {
                                console.error('Error marking invoice as paid:', error);
                                alert('Failed to mark invoice as paid');
                              }
                            }
                          }}
                        >
                          Mark Paid
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={async () => {
                            if (window.confirm('Cancel this invoice?')) {
                              try {
                                const token = localStorage.getItem('token');
                                await axios.put(
                                  `/api/invoices/${invoice.id}`,
                                  {
                                    ...invoice,
                                    status: 'cancelled'
                                  },
                                  {
                                    headers: { 'Authorization': `Bearer ${token}` }
                                  }
                                );

                                // Refresh invoices list
                                await fetchInvoices();
                              } catch (error) {
                                console.error('Error cancelling invoice:', error);
                                alert('Failed to cancel invoice');
                              }
                            }
                          }}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                    <IconButton
                      size="small"
                      title="Delete invoice"
                      onClick={(e) => handleDeleteClick(invoice, e)}
                      sx={{ color: 'error.main' }}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
                );
              })}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredInvoices.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            Do you really want to delete this invoice?
          </Typography>
          {invoiceToDelete && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Invoice #{invoiceToDelete.invoice_number}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteDialogOpen(false);
              setInvoiceToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleDeleteConfirm}
          >
            Delete Invoice
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}